/**
 * Handles the user click on a line in a joke.
 *
 * Inputs:
 * - bitIndex: The index of the bit in the processedBitsArray.
 * - jokeIndex: The index of the joke within the bit.
 * - lineIndex: The index of the line within the joke.
 * - processedBitsArray: The array of categorized bits (array of arrays of jokes).
 *
 * Output:
 * - Allows users to assign or unassign a punchline by clicking on any line (except the first line).
 * - Combines all taglines into a single tagline.
 * - Returns the updated array of categorized bits.
 */

export const handleLineClick = (
  bitIndex,
  jokeIndex,
  lineIndex,
  processedBitsArray
) => {
  // console.log(`Clicked on bitIndex: ${bitIndex}, jokeIndex: ${jokeIndex}, lineIndex: ${lineIndex}`);

  const updatedBitsArray = [...processedBitsArray];
  const joke = updatedBitsArray[bitIndex][jokeIndex];

  if (!joke) {
    // console.error('Invalid joke');
    return processedBitsArray;
  }

  let jokeLines;

  // Reconstruct jokeLines depending on whether joke is an object or a string
  if (typeof joke === 'object') {
    const { punchline, setup, taglines } = joke;
    jokeLines = [...setup, punchline.text, ...taglines.map(tagline => tagline.punchline.text)];
  } else if (typeof joke === 'string') {
    jokeLines = joke.split('\n');
  } else {
    // console.error('Invalid joke type');
    return processedBitsArray;
  }

  // Validate lineIndex
  if (lineIndex < 0 || lineIndex >= jokeLines.length) {
    // console.error('Invalid lineIndex');
    return processedBitsArray;
  }

  // Ensure the first line is always a setup and unclickable
  if (lineIndex === 0) return updatedBitsArray;

  // Now proceed to process the click
  if (typeof joke === 'object') {
    // If the clicked line is already the punchline, undo the punchline assignment
    if (lineIndex === joke.setup.length) {
      const restoredJoke = jokeLines.join('\n');
      updatedBitsArray[bitIndex][jokeIndex] = restoredJoke;
    } else {
      // Reassign the punchline to the clicked line
      const newSetup = jokeLines.slice(0, lineIndex);
      const newPunchline = { text: jokeLines[lineIndex] };

      // Create taglines only if there's actual text after the punchline
      const remainingLines = jokeLines.slice(lineIndex + 1).filter(line => line.trim() !== '');
      const newTaglines = remainingLines.length > 0 ? [{
        punchline: { text: remainingLines.join('\n') },
      }] : [];

      // Replace the joke object
      updatedBitsArray[bitIndex][jokeIndex] = {
        setup: newSetup,
        punchline: newPunchline,
        taglines: newTaglines,
      };
    }
  } else {
    // For uncategorized joke (string)
    // Construct the joke object
    const newSetup = jokeLines.slice(0, lineIndex);
    const newPunchline = { text: jokeLines[lineIndex] };

    // Create taglines if any
    const remainingLines = jokeLines.slice(lineIndex + 1).filter(line => line.trim() !== '');
    const newTaglines = remainingLines.length > 0 ? [{
      punchline: { text: remainingLines.join('\n') },
    }] : [];

    // Replace the joke string with the joke object
    updatedBitsArray[bitIndex][jokeIndex] = {
      setup: newSetup,
      punchline: newPunchline,
      taglines: newTaglines,
    };
  }

  return updatedBitsArray;
};
